/**
 * Bulk Disbursement Tour Configuration
 * 
 * Tour guide for the Bulk Disbursement tab only.
 */

export const getBulkDisbursementTourSteps = (t) => {
  // Helper function to check if an element exists in the DOM
  const elementExists = (selector) => {
    if (typeof document === 'undefined') {return false;}
    const element = document.querySelector(selector);
    return element !== null && element.offsetParent !== null;
  };

  // Base steps that always appear
  const baseSteps = [
    // Tabs section - positioned to highlight the tab buttons
    {
      selector: '[data-tour="disbursements-tabs"]',
      content: t('tour.disbursements.tabs', {
        ns: 'common',
        defaultValue: 'Choose between Single or Bulk disbursement mode.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '-10px'
        }),
        maskWrapper: (base) => ({
          ...base,
          transform: 'translateY(-27px)'
        }),
        maskArea: (base) => ({
          ...base,
          transform: 'translateY(-27px)'
        })
      }
    }
  ];

  // Bulk disbursement steps - Different workflow from single
  const bulkSteps = [
    // Bulk Upload Section - First step in bulk workflow
    {
      selector: '[data-tour="disbursements-bulk-upload"]',
      content: t('tour.disbursements.bulkUpload', {
        ns: 'common',
        defaultValue: 'Download CSV template, fill with recipient details, then upload here.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px',
          maxWidth: 'min(400px, calc(100vw - 320px))',
          minLeft: '270px', // Sidebar (250px) + padding (20px)
          position: 'fixed'
        }),
        maskWrapper: (base) => ({
          ...base,
          left: '250px' // Ensure mask doesn't cover sidebar
        })
      }
    },
    
    // Bulk Preview - Only shown when file is uploaded and parsed
    {
      selector: '[data-tour="disbursements-bulk-preview"]',
      content: t('tour.disbursements.bulkPreview', {
        ns: 'common',
        defaultValue: 'Review all disbursements, totals, and fees before processing.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px',
          maxWidth: 'min(400px, calc(100vw - 320px))',
          minLeft: '270px', // Sidebar (250px) + padding (20px)
          position: 'fixed'
        }),
        maskWrapper: (base) => ({
          ...base,
          left: '250px' // Ensure mask doesn't cover sidebar
        })
      }
    },
    
    // Batch Number Input - Only shown when file is uploaded and parsed
    {
      selector: '[data-tour="disbursements-bulk-batch"]',
      content: t('tour.disbursements.bulkBatch', {
        ns: 'common',
        defaultValue: 'Enter a batch number to identify this disbursement batch. This is required for processing.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px',
          maxWidth: 'min(400px, calc(100vw - 320px))',
          minLeft: '270px', // Sidebar (250px) + padding (20px)
          position: 'fixed'
        }),
        maskWrapper: (base) => ({
          ...base,
          left: '250px' // Ensure mask doesn't cover sidebar
        })
      }
    },
    
    // Bulk Actions - Submit and Cancel buttons
    {
      selector: '[data-tour="disbursements-bulk-actions"]',
      content: t('tour.disbursements.bulkActions', {
        ns: 'common',
        defaultValue: 'Review everything, then submit to process all disbursements, or cancel to start over.'
      }),
      position: 'top',
      styles: {
        popover: (base) => ({
          ...base,
          marginBottom: '10px',
          maxWidth: 'min(400px, calc(100vw - 320px))',
          minLeft: '270px', // Sidebar (250px) + padding (20px)
          position: 'fixed'
        }),
        maskWrapper: (base) => ({
          ...base,
          left: '250px' // Ensure mask doesn't cover sidebar
        })
      }
    }
  ];

  // History list (always shown)
  const historyStep = {
    selector: '[data-tour="disbursements-list"]',
    content: t('tour.disbursements.list', {
      ns: 'common',
      defaultValue: 'View and manage past disbursements.'
    }),
    position: 'bottom'
  };

  // Build steps array - filter to only include existing elements
  const allSteps = [...baseSteps];
  allSteps.push(...bulkSteps.filter(step => elementExists(step.selector)));
  
  // Always add history step if it exists
  if (elementExists(historyStep.selector)) {
    allSteps.push(historyStep);
  }

  return allSteps;
};

